import {
  DEFAULT_GAME_LANG,
  isGameLang,
  isWordLength,
  type GameLang,
  type WordLength,
} from '@/modules/wordle/game/types';

const THEME_KEY = 'deytanplay:theme';
const LOCALE_KEY = 'deytanplay:locale';
const NICK_KEY = 'deytanplay:nick';
const WORDLE_LENGTH_KEY = 'deytanplay:wordle:length';
const WORDLE_GAME_LANG_KEY = 'deytanplay:wordle:gameLang';
const wordleKey = (id: string) => `deytanplay:wordle:${id}`;

export type ThemePreference = 'system' | 'light' | 'dark';
export type LocaleCode = 'pt' | 'en';

export const DEFAULT_WORDLE_LENGTH: WordLength = 5;
export { DEFAULT_GAME_LANG };

export interface WordleLocalRecord {
  guessCount: number;
  nick?: string;
}

function canUseStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

export function getThemePreference(): ThemePreference {
  if (!canUseStorage()) return 'system';
  const value = localStorage.getItem(THEME_KEY);
  if (value === 'light' || value === 'dark' || value === 'system') return value;
  return 'system';
}

export function setThemePreference(theme: ThemePreference): void {
  if (!canUseStorage()) return;
  localStorage.setItem(THEME_KEY, theme);
}

export function detectBrowserLocale(): LocaleCode {
  if (typeof navigator === 'undefined') return 'pt';
  const lang = navigator.language.toLowerCase();
  return lang.startsWith('en') ? 'en' : 'pt';
}

export function getLocalePreference(): LocaleCode | null {
  if (!canUseStorage()) return null;
  const value = localStorage.getItem(LOCALE_KEY);
  if (value === 'pt' || value === 'en') return value;
  return null;
}

export function setLocalePreference(locale: LocaleCode): void {
  if (!canUseStorage()) return;
  localStorage.setItem(LOCALE_KEY, locale);
}

export function getNickPreference(): string {
  if (!canUseStorage()) return '';
  return localStorage.getItem(NICK_KEY) ?? '';
}

export function setNickPreference(nick: string): void {
  if (!canUseStorage()) return;
  const trimmed = nick.trim();
  if (trimmed) {
    localStorage.setItem(NICK_KEY, trimmed);
  } else {
    localStorage.removeItem(NICK_KEY);
  }
}

export function getWordleLengthPreference(): WordLength {
  if (!canUseStorage()) return DEFAULT_WORDLE_LENGTH;
  const raw = localStorage.getItem(WORDLE_LENGTH_KEY);
  if (raw === null) return DEFAULT_WORDLE_LENGTH;
  const parsed = Number(raw);
  if (isWordLength(parsed)) return parsed;
  return DEFAULT_WORDLE_LENGTH;
}

export function setWordleLengthPreference(length: WordLength): void {
  if (!canUseStorage()) return;
  localStorage.setItem(WORDLE_LENGTH_KEY, String(length));
}

export function getWordleGameLangPreference(): GameLang {
  if (!canUseStorage()) return DEFAULT_GAME_LANG;
  const raw = localStorage.getItem(WORDLE_GAME_LANG_KEY);
  if (isGameLang(raw)) return raw;
  return DEFAULT_GAME_LANG;
}

export function setWordleGameLangPreference(lang: GameLang): void {
  if (!canUseStorage()) return;
  localStorage.setItem(WORDLE_GAME_LANG_KEY, lang);
}

export function getWordleRecord(id: string): WordleLocalRecord | null {
  if (!canUseStorage()) return null;
  const raw = localStorage.getItem(wordleKey(id));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as WordleLocalRecord;
    if (typeof parsed.guessCount !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveWordleRecord(id: string, record: WordleLocalRecord): void {
  if (!canUseStorage()) return;
  localStorage.setItem(wordleKey(id), JSON.stringify(record));
}

export function touchWordleGuessCount(id: string, guessCount: number, nick?: string): void {
  const existing = getWordleRecord(id);
  saveWordleRecord(id, {
    guessCount: Math.max(existing?.guessCount ?? 0, guessCount),
    nick: nick ?? existing?.nick,
  });
}

export function isStaleWordleLink(id: string, guessCount: number): boolean {
  const existing = getWordleRecord(id);
  if (!existing) return false;
  return guessCount < existing.guessCount;
}
